import { useState, useEffect } from "react";
import { supabase } from "../../../../../../integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../../../components/ui/card";
import { Badge } from "../../../../../ui/badge";
import { Skeleton } from "../../../../../ui/skeleton";
import { Switch } from "../../../../../ui/switch";
import { Label } from "../../../../../ui/label";
import { Button } from "../../../../../ui/button";
import { KeywordReviewsModal } from "./KeywordReviewsModal";
import ReactWordcloud from "react-wordcloud";
import { Cloud, List } from "lucide-react";

interface TopKeywordsProps {
  dateRange: string;
  platform: string;
  outletIds?: string[];
  supabaseoutletIDs?: string[],
}

interface WordData {
  text: string;
  value: number;
  sentiment: number;
}

export const TopKeywords = ({ dateRange, platform, outletIds, supabaseoutletIDs }: TopKeywordsProps) => {
  const [loading, setLoading] = useState(true);
  const [keywords, setKeywords] = useState<WordData[]>([]);
  const [topics, setTopics] = useState<WordData[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ value: string; type: "keyword" | "topic" } | null>(null);
  const [sentimentOverlay, setSentimentOverlay] = useState(true);
  const [viewMode, setViewMode] = useState<"cloud" | "list">("cloud");
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    loadData();
  }, [dateRange, platform, outletIds]);

  const loadData = async () => {
    setLoading(true);
    try {
      const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      let query = supabase
        .from("reviews")
        .select("comment_text, topics, sentiment, sentiment_score")
        .gte("posted_at", startDate.toISOString());

      if (platform !== "all") {
        query = query.eq("platform", platform as any);
      }

      const isAllSelected = !outletIds || outletIds.length === 0 || outletIds.includes("all");
      if (!isAllSelected && outletIds.length > 0) {
        query = query.in("outlet_id", outletIds);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs);
      }

      const { data: reviews, error } = await query;
      if (error) throw error;

      // Enhanced stopwords list
      const stopWords = new Set([
        "they", "their", "very", "from", "this", "that", "was", "were", "is", "are", 
        "it", "the", "a", "an", "of", "and", "to", "in", "on", "for", "as", "with",
        "have", "has", "had", "will", "would", "could", "should", "been", "being",
        "there", "here", "when", "where", "what", "which", "who", "how", "just",
        "also", "can", "about", "into", "than", "them", "then", "some", "make",
        "like", "more", "been", "only", "other", "such", "even", "most", "well"
      ]);
      
      // Extract keywords with sentiment
      const wordData: Record<string, { count: number; sentiments: number[] }> = {};
      
      reviews?.forEach((r) => {
        const words = r.comment_text?.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
        const sentiment = r.sentiment_score || 0;
        
        words.forEach((word) => {
          if (!stopWords.has(word)) {
            if (!wordData[word]) {
              wordData[word] = { count: 0, sentiments: [] };
            }
            wordData[word].count += 1;
            wordData[word].sentiments.push(sentiment);
          }
        });
      });

      // Calculate average sentiment per word
      const topKeywords: WordData[] = Object.entries(wordData)
        .map(([word, data]) => ({
          text: word,
          value: data.count,
          sentiment: data.sentiments.reduce((a, b) => a + b, 0) / data.sentiments.length
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 30);

      // Extract topics with sentiment
      const topicData: Record<string, { count: number; sentiments: number[] }> = {};
      
      reviews?.forEach((r) => {
        const sentiment = r.sentiment_score || 0;
        r.topics?.forEach((topic: string) => {
          if (!topicData[topic]) {
            topicData[topic] = { count: 0, sentiments: [] };
          }
          topicData[topic].count += 1;
          topicData[topic].sentiments.push(sentiment);
        });
      });

      const topTopics: WordData[] = Object.entries(topicData)
        .map(([topic, data]) => ({
          text: topic,
          value: data.count,
          sentiment: data.sentiments.reduce((a, b) => a + b, 0) / data.sentiments.length
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 30);

      setKeywords(topKeywords);
      setTopics(topTopics);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error loading keywords:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment: number) => {
    // sentiment_score ranges from 0 to 1
    // 0-0.4 = negative (red), 0.4-0.6 = neutral (gray), 0.6-1.0 = positive (green)
    if (sentiment >= 0.6) {
      // Positive: gradient from light to dark green
      const intensity = (sentiment - 0.6) / 0.4;
      return `hsl(142, ${70 + intensity * 30}%, ${60 - intensity * 25}%)`;
    } else if (sentiment <= 0.4) {
      // Negative: gradient from light to dark red
      const intensity = (0.4 - sentiment) / 0.4;
      return `hsl(0, ${70 + intensity * 30}%, ${60 - intensity * 25}%)`;
    }
    // Neutral: gray
    return "hsl(var(--muted-foreground))";
  };

  const getFrequencyColor = (value: number, maxValue: number) => {
    const intensity = value / maxValue;
    return `hsl(217, 91%, ${70 - intensity * 30}%)`;
  };

  const getWordColor = (word: WordData) => {
    return sentimentOverlay
      ? getSentimentColor(word.sentiment)
      : getFrequencyColor(word.value, Math.max(...keywords.map(k => k.value)));
  };

  const getTopicColor = (topic: WordData) => {
    return sentimentOverlay
      ? getSentimentColor(topic.sentiment)
      : getFrequencyColor(topic.value, Math.max(...topics.map(t => t.value)));
  };

  const wordCloudOptions = {
    rotations: 0,
    fontSizes: [14, 48] as [number, number],
    fontFamily: "Inter",
    enableTooltip: true,
    padding: 4,
    colors: keywords.map(w => getWordColor(w)),
    transitionDuration: 500,
    deterministic: true,
  };

  const topicCloudOptions = {
    rotations: 0,
    fontSizes: [14, 48] as [number, number],
    fontFamily: "Inter",
    enableTooltip: true,
    padding: 4,
    colors: topics.map(t => getTopicColor(t)),
    transitionDuration: 500,
    deterministic: true,
  };

  const handleWordClick = (word: { text: string }) => {
    setSelectedItem({ value: word.text, type: "keyword" });
    setModalOpen(true);
  };

  const handleTopicClick = (word: { text: string }) => {
    setSelectedItem({ value: word.text, type: "topic" });
    setModalOpen(true);
  };

  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 60) return "Updated just now";
    if (seconds < 3600) return `Updated ${Math.floor(seconds / 60)} minutes ago`;
    return `Updated ${Math.floor(seconds / 3600)} hours ago`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="sentiment-overlay"
              checked={sentimentOverlay}
              onCheckedChange={setSentimentOverlay}
            />
            <Label htmlFor="sentiment-overlay">Sentiment Overlay</Label>
          </div>
          <div className="flex items-center gap-1 border rounded-md">
            <Button
              variant={viewMode === "cloud" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("cloud")}
            >
              <Cloud className="h-4 w-4 mr-1" />
              Cloud
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{getTimeSinceUpdate()}</p>
      </div>

      {sentimentOverlay && (
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-[hsl(142,100%,45%)]"></span>
            Positive
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(var(--muted-foreground))" }}></span>
            Neutral
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-[hsl(0,100%,50%)]"></span>
            Negative
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top Keywords</CardTitle>
            <CardDescription>Most frequently mentioned words across all customer reviews</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : viewMode === "cloud" ? (
              <div className="h-64 animate-fade-in">
                <ReactWordcloud
                  words={keywords}
                  options={wordCloudOptions}
                  callbacks={{
                    onWordClick: handleWordClick,
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {keywords.map((word) => (
                  <Badge
                    key={word.text}
                    variant="secondary"
                    className="text-sm cursor-pointer hover:bg-secondary/80 transition-colors"
                    style={{
                      backgroundColor: sentimentOverlay
                        ? getSentimentColor(word.sentiment)
                        : getFrequencyColor(word.value, Math.max(...keywords.map(k => k.value))),
                      color: "white",
                    }}
                    onClick={() => handleWordClick(word)}
                  >
                    {word.text} ({word.value})
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Topics</CardTitle>
            <CardDescription>Common themes customers discuss</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : viewMode === "cloud" ? (
              <div className="h-64 animate-fade-in">
                <ReactWordcloud
                  words={topics}
                  options={topicCloudOptions}
                  callbacks={{
                    onWordClick: handleTopicClick,
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <Badge
                    key={topic.text}
                    variant="outline"
                    className="text-sm cursor-pointer hover:bg-accent transition-colors"
                    style={{
                      backgroundColor: sentimentOverlay
                        ? getSentimentColor(topic.sentiment)
                        : getFrequencyColor(topic.value, Math.max(...topics.map(t => t.value))),
                      color: "white",
                    }}
                    onClick={() => handleTopicClick(topic)}
                  >
                    {topic.text} ({topic.value})
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedItem && (
        <KeywordReviewsModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedItem(null);
          }}
          keyword={selectedItem.value}
          type={selectedItem.type}
          dateRange={dateRange}
          platform={platform}
          outletIds={outletIds}
          supabaseoutletIDs={supabaseoutletIDs}
        />
      )}
    </div>
  );
};
